import { AppDispatch } from "@/store";

interface RequestState {
  isLoading: boolean;
  timestamp: number;
  promise?: Promise<any>;
}

class GlobalRequestManager {
  private static instance: GlobalRequestManager;
  private requestStates: Map<string, RequestState> = new Map();
  private readonly TTL = 2000;

  static getInstance(): GlobalRequestManager {
    if (!GlobalRequestManager.instance) {
      GlobalRequestManager.instance = new GlobalRequestManager();
    }
    return GlobalRequestManager.instance;
  }

  private generateKey(thunkType: string, params: any): string {
    return `${thunkType}-${JSON.stringify(params)}`;
  }

  private isRequestBlocked(key: string): boolean {
    const state = this.requestStates.get(key);
    if (!state) return false;

    const now = Date.now();
    const isRecent = now - state.timestamp < this.TTL;

    return state.isLoading || isRecent;
  }

  async interceptDispatch<T>(
    dispatch: AppDispatch,
    thunk: any,
    params?: any,
  ): Promise<T | null> {
    const thunkType = thunk.typePrefix || thunk.type;
    const key = this.generateKey(thunkType, params);

    if (this.isRequestBlocked(key)) {
      const existingState = this.requestStates.get(key);
      if (existingState?.promise) {
        if (process.env.NODE_ENV === "development") {
          // eslint-disable-next-line no-console
          console.log(`🚫 Requisição bloqueada (duplicata): ${thunkType}`);
        }
        return existingState.promise;
      }
      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.log(`⏰ Requisição bloqueada (recente): ${thunkType}`);
      }
      return null;
    }

    const promise = dispatch(thunk(params));
    this.requestStates.set(key, {
      isLoading: true,
      timestamp: Date.now(),
      promise,
    });

    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.log(`✅ Executando requisição: ${thunkType}`);
    }

    try {
      const result = await promise;

      this.requestStates.set(key, {
        isLoading: false,
        timestamp: Date.now(),
        promise: undefined,
      });

      return result;
    } catch (error) {
      this.requestStates.delete(key);
      throw error;
    }
  }

  cleanup(): void {
    const now = Date.now();
    for (const [key, state] of this.requestStates.entries()) {
      if (!state.isLoading && now - state.timestamp > this.TTL * 2) {
        this.requestStates.delete(key);
      }
    }
  }

  clearRequest(thunkType: string, params: any): void {
    const key = this.generateKey(thunkType, params);
    this.requestStates.delete(key);
  }

  getStats(): { total: number; loading: number; cached: number } {
    let loading = 0;
    let cached = 0;

    for (const state of this.requestStates.values()) {
      if (state.isLoading) loading++;
      else cached++;
    }

    return {
      total: this.requestStates.size,
      loading,
      cached,
    };
  }
}

export const globalRequestManager = GlobalRequestManager.getInstance();

export const useGlobalRequestManager = () => {
  return {
    interceptDispatch:
      globalRequestManager.interceptDispatch.bind(globalRequestManager),
    clearRequest: globalRequestManager.clearRequest.bind(globalRequestManager),
    cleanup: globalRequestManager.cleanup.bind(globalRequestManager),
    getStats: globalRequestManager.getStats.bind(globalRequestManager),
  };
};

setInterval(() => {
  globalRequestManager.cleanup();
}, 30000);

export default globalRequestManager;
